import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";

import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Input from "@mui/material/Input";
import InputAdornment from "@mui/material/InputAdornment";
import InputLabel from "@mui/material/InputLabel";
import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

import { asTitle, isEmpty, isPasswordValid } from "utils/Utils";

import { resetPassword } from "authentication/AuthenticationService";

const ResetPasswordPage: React.FC = () => {
	const { t } = useTranslation();
	const location = useLocation();

	const [password, setPassword] = useState("");
	const [showPassword, setShowPassword] = useState(false);
	const [passwordValid, setPasswordValid] = useState(true);
	const [passwordRepeat, setPasswordRepeat] = useState("");
	const [passwordRepeatValid, setPasswordRepeatValid] = useState(true);
	const [loading, setLoading] = useState(false);
	const [resetFailed, setResetFailed] = useState(false);
	const [resetSucceeded, setResetSucceeded] = useState(false);

	function onPasswordChange(
		event: React.ChangeEvent<HTMLInputElement>
	): void {
		event.preventDefault();
		const passwordValue = event.target.value;
		setPassword(passwordValue);
		setPasswordValid(!isEmpty(passwordValue));
	}

	function onPasswordRepeatChange(
		event: React.ChangeEvent<HTMLInputElement>
	): void {
		event.preventDefault();
		const passwordRepeatValue = event.target.value;
		setPasswordRepeat(passwordRepeatValue);
		setPasswordRepeatValid(!isEmpty(passwordRepeatValue));
	}

	function onShowPassword() {
		setShowPassword(!showPassword);
	}

	function isFormValid(): boolean {
		if (!isPasswordValid(password)) {
			setPasswordValid(false);
			return false;
		}

		if (passwordRepeat !== password) {
			setPasswordRepeatValid(false);
			return false;
		}

		return true;
	}

	function submit(
		event: React.MouseEvent<HTMLButtonElement, MouseEvent>
	): void {
		event.preventDefault();

		setResetFailed(false);
		setResetSucceeded(false);
		const code = new URLSearchParams(location.search).get("code");

		if (code === null) {
			return;
		}

		if (isFormValid()) {
			if (!loading) {
				setLoading(true);
				resetPassword(code, password)
					.then(() => {
						setResetSucceeded(true);
						setPassword("");
						setPasswordRepeat("");
					})
					.catch(() => setResetFailed(true))
					.finally(() => setLoading(false));
			}
		}
	}

	return (
			<Grid container direction="row" justifyContent="center">
				<Grid item xs={12} sm={8} md={6} lg={4}>
					<Paper
						sx={{
							margin: 2,
							padding: 2,
							textAlign: "center"
						}}>
						<Typography variant="h5" gutterBottom>
							{asTitle(t("resetPasswordTitle"))}
						</Typography>
						{resetFailed && (
							<Alert id="error-message" severity="error">
								{t("resetPasswordFailed")}
							</Alert>
						)}
						{resetSucceeded && (
							<Alert id="success-message" severity="success">
								{t("resetPasswordSuccess")}
							</Alert>
						)}
						<Box
							component="form"
							sx={{
								"& .MuiTextField-root, .MuiButton-root, .MuiFormControl-root":
									{
										marginTop: 1,
										marginBottom: 1
									}
							}}>
							<FormControl required fullWidth variant="standard">
								<InputLabel htmlFor="password">
									{t("password")}
								</InputLabel>
								<Input
									id="password"
									type={showPassword ? "text" : "password"}
									error={!passwordValid}
									value={password}
									onChange={onPasswordChange}
									endAdornment={
										<InputAdornment position="end">
											<IconButton
												aria-label="toggle password visibility"
												onClick={onShowPassword}
												onMouseDown={e =>
													e.preventDefault()
												}
												size="large">
												{showPassword ? (
													<Visibility />
												) : (
													<VisibilityOff />
												)}
											</IconButton>
										</InputAdornment>
									}
								/>
								<FormHelperText>
									{t("passwordRestrictions")}
								</FormHelperText>
							</FormControl>
							<TextField
								id="passwordRepeat"
								type={"password"}
								required
								fullWidth
								error={!passwordRepeatValid}
								value={passwordRepeat}
								label={t("passwordRepeat")}
								helperText={
									passwordRepeatValid
										? ""
										: t("passwordsDoNotMatch")
								}
								variant="standard"
								onChange={onPasswordRepeatChange}
							/>
							<Button
								id="login"
								variant="contained"
								fullWidth
								disabled={loading}
								onClick={submit}>
								{t("resetPasswordButton")}
							</Button>
							<Typography variant="body1">
								<Link to="/login">{t("toLogin")}</Link>
							</Typography>
						</Box>
					</Paper>
				</Grid>
			</Grid>
	);
};

export default ResetPasswordPage;
